<template>
  <div>
    <h3 class="mt-2">Logs</h3>
    <top-table
      :showLoadAllData="true"
      :tabFilters="options.tabFilters"
      :columns="['userId', 'category', 'notes', 'changes', 'parent', 'parentCollection', 'createdAt', 'ip', 'service']"
      :headings="options.headings"
      :sortable="['category', 'notes', 'parentCollection', 'ip', 'createdAt']"
      :order-by="{ column: 'createdAt', ascending: 0 }"
      :searchColumns="['notes']"
      url="/logs"
      :request-function="onBeforeReq"
      :overlap="true"
      :hasClearFilters="true"
    >
      <span slot="customAfterFilter" slot-scope="props"
        ><i class="fas fa-question-circle ml-2" v-b-tooltip.hover :title="'Search by notes, mongo hash Id'" />
      </span>
      <template v-slot:service="props">
        {{ props.row.env || '' }}
      </template>
      <template v-slot:userId="props">
        <b-avatar
          v-if="props.row.isSystem"
          variant="warning"
          :text="`MP`"
          :title="`MPower`"
          src="/img/mpower_mark.svg"
        ></b-avatar>
        <span v-else>
          {{
            [props.row.userId && props.row.userId.firstName, props.row.userId && props.row.userId.lastName]
              .filter((e) => e)
              .join(' ')
          }}
        </span>
      </template>
      <template v-slot:category="props">
        <LogView :log="props.row" />
      </template>
      <template v-slot:notes="props">
        {{ props.row.category === 'PRIVATE_DATA_RESPONSE' ? groupResponseUrls(props.row) : props.row.notes }}
      </template>
      <template v-slot:parentCollection="props">
        {{ (props.row.orderId && 'orders') || props.row.parentCollection || '-' }}
      </template>
      <template v-slot:parent="props">
        <router-link v-if="props.row.parent" :to="`/${props.row.parentCollection}/${props.row.parent._id}`">{{
          [props.row.parent.firstName, props.row.parent.lastName].filter((e) => e).join(' ')
        }}</router-link>
        <router-link v-else-if="props.row.orderId" :to="`/admin/orders/${props.row.orderId.orderId}`">{{
          props.row.orderId && [props.row.orderId.orderId, props.row.purchaseId].filter((e) => e).join(', ')
        }}</router-link>
      </template>
      <template v-slot:createdAt="props">
        <span>{{ props.row.createdAt | dateWithTime }}</span>
      </template>
      <template v-slot:changes="props">
        <div
          v-if="props.row.changes.length"
          :title="props.row.changes.map((e) => `${e.attribute}->${e.newValue || ''}`).join(', ')"
          style="text-overflow: ellipsis; overflow: hidden"
        >
          {{ props.row.changes.map((e) => `${e.attribute}-${e.oldValue || ''}->${e.newValue || ''}`).join(', ') }}
        </div>
        <div v-if="props.row.details" :title="props.row.details" style="text-overflow: ellipsis; overflow: hidden">
          <span>{{ props.row.details }}</span>
          <PreviewRequest
            :details="detailsBody(props.row.details)"
            v-if="detailsBody(props.row.details)"
            :fullScreen="true"
          >
            <template v-slot:button="props">
              <a @click="props.onShowDetails" class="btn btn-lg btn-link p-0">
                <i class="far fa-info-circle"></i>
              </a> </template
          ></PreviewRequest>
        </div>
        <div v-if="props.row.body?.length" :title="props.row.body" style="text-overflow: ellipsis; overflow: hidden">
          <span> {{ props.row.body }}</span>
          <PreviewRequest :details="detailsBody(props.row.body)" :fullScreen="true">
            <template v-slot:button="props">
              <a @click="props.onShowDetails" class="btn btn-lg btn-link p-0">
                <i class="far fa-info-circle"></i>
              </a> </template
          ></PreviewRequest>
        </div>
      </template>
    </top-table>
  </div>
</template>
<script>
const CATEGORY = [
  'INVOICE',
  'RECEIPT',
  'ORDER_UPDATES',
  'ORDER_PURCHASE_UPDATES',
  'ORDER_PROOF_REQUEST_UPDATES',
  'ORDER_PAYMENT',
  'EMAILS',
  'SLACK_NOTIFICATION',
  'CUSTOMER_UPDATES',
  'SSO_ERRORS',
  'ORDER_SALES_ASSIGN',
  'ORDER_ADMIN_ASSIGN',
  'CUSTOMER_SALES_ASSIGN',
  'CUSTOMER_ADMIN_ASSIGN',
  'ORDER_ESTIMATE_SHIPPING',
  'ORDER_DISCOUNT',
  'UPDATE',
  'TRANSACTIONAL_EMAIL',
  'CUSTOMER_REPORT_BUILD',
  'SEND_EMAIL',
  'PRIVATE_DATA_RESPONSE',
  'CUSTOMER_SSO_LOG_IN',
  'FROALA_IMAGE_UPLOAD_ERRORS',
  'PRODUCT_RE_CALC_PRICE',
  'QUOTE_SENT',
  'ESTIMATE_SHIPPING_ERRORS',
  'ESTIMATE_TAX_ERRORS',
  'RECAPTCHA_ERRORS',
  'PAYMENT_ERRORS',
  'XERO_ERRORS',
  'QUICKBOOKS_ERRORS',
  'MODEL_CREATE',
  'MODEL_UPDATE',
  'MODEL_REMOVED',
  'CREATE_ORDER_ERRORS',
  'UPDATE_ORDER_ERRORS',
  'ZOHO_ERRORS'
];
import { encodeOpts } from '../../../utils/encode-opts';
export default {
  name: 'Logs',
  props: {
    filter: {
      type: Object
    }
  },
  components: {
    PreviewRequest: () => import('../../../components/Grabber/PreviewRequest'),
    LogView: () => import('./LogView')
  },
  data() {
    return {
      options: {
        tabFilters: [
          {
            val: 'categories',
            name: 'Category',
            criteria: [],
            pathToLoadCriteria: `/logs?onlyCategories=1&noData=1${this.filter && `&${encodeOpts(this.filter)}`}`,
            mapCriteria: (el) => ({
              val: el._id,
              label: `${el._id}(${el.total})`
            })
          },
          !this.filter && {
            val: 'parentCollection',
            name: 'Collection',
            criteria: [],
            pathToLoadCriteria: `/logs?onlyCollections=1&noData=1`,
            mapCriteria: (el) => ({
              val: el._id,
              label: `${el._id}(${el.total})`
            })
          },
          {
            val: 'userId',
            name: 'UserId',
            criteria: [],
            pathToLoadCriteria: `/logs/userId/models${this.filter && `?${encodeOpts(this.filter)}`}`,
            paramsToLoadCriteria: {
              limit: 25,
              filter: {}
            },
            searchColumns: [],
            mapCriteria: (el) => ({
              label: [
                (el.userId && el.userId.firstName) || '-',
                el.userId && el.userId.lastName,
                el.sum && `(${el.sum})`
              ]
                .filter((e) => e)
                .join(' '),
              name: [
                (el.userId && el.userId.firstName) || '-',
                el.userId && el.userId.lastName,
                el.sum && `(${el.sum})`
              ]
                .filter((e) => e)
                .join(' '),
              value: el.userId && el.userId._id
            })
          },
          {
            val: 'notes',
            name: 'Notes',
            criteria: [],
            pathToLoadCriteria: `/logs/notes/models${this.filter && `?${encodeOpts(this.filter)}`}`,
            searchColumns: [],
            paramsToLoadCriteria: {
              limit: 25,
              filter: {}
            },
            mapCriteria: (el) => ({
              label: [el._id, el.sum && `(${el.sum})`].filter((e) => e).join(' '),
              name: [el._id, el.sum && `(${el.sum})`].filter((e) => e).join(' '),
              value: el._id
            })
          },
          !this.filter && {
            val: 'parent',
            label: 'parent',
            name: 'Parent',
            criteria: [],
            pathToLoadCriteria: `/logs/parent/models`,
            searchColumns: [],
            paramsToLoadCriteria: {
              limit: 25,
              filter: {}
            },
            mapCriteria: (el) => ({
              label:
                (el.parent_customer[0] && [el.parent.firstName, el.parent.lastName].filter((e) => e).join(' ')) ||
                (el.parent_order[0] && el.parent.orderId) ||
                '',
              name:
                (el.parent_customer[0] && [el.parent.firstName, el.parent.lastName].filter((e) => e).join(' ')) ||
                (el.parent_order[0] && el.parent.orderId) ||
                '',
              value: el._id
            })
          }
        ].filter((e) => e),
        headings: {
          createdBy: 'createdBy',
          category: 'category'
        }
      }
    };
  },
  methods: {
    detailsBody(body) {
      try {
        return { request: JSON.stringify(JSON.parse(body), null, 2) };
      } catch (e) {
        return null;
      }
    },
    onBeforeReq(p) {
      if (this.filter) {
        Object.assign(p, this.filter);
        p.withParent = true;
      } else {
        p.withOrder = true;
        p.withStore = true;
        p.withUser = true;
        p.withCustomer = true;
        p.withParent = true;
        p.withoutDistributor = true;
      }

      p.fields = ['notes'];
    },
    groupResponseUrls({ info }) {
      const urls = (info.history || []).reduce((urls, i) => {
        urls.set(`${i.url}-${i.method}`, i);
        return urls;
      }, new Map());
      return `Links: ${Array.from(urls.values()).length}`;
    }
  }
};
</script>
